/**
 * Staff calendar-sync panel (DNKADM-0384 U2/U3) — the one control on the staff record
 * that connects a coach to a named calendar, and the test action that proves it.
 *
 * **U2 — connected by name, never by id.** Calendar sync is off, or on and pointed at a
 * calendar chosen from a picker that lists what the tenant's active provider offers
 * (`GET /v2/admin/staff/{id}/calendars`, DNKAPI-0538 U2) BY NAME. No identifier is ever
 * typed or displayed — the stored `calendarId` is matched against the list to show the
 * selection, and only an id the picker offered is ever written back. The rules:
 *  - **On without a calendar cannot be saved** — the Save button is disabled, so the
 *    form never offers the state the server rejects (`STAFF_CALENDAR_SYNC_NEEDS_CALENDAR`).
 *  - **Off keeps the selection**, and the screen says so: the id is only sent when the
 *    operator picked a different calendar; turning sync off sends the flag alone.
 *  - **A stored selection missing from the current provider's list** (the tenant
 *    switched providers) shows as *needs re-selection*, never as configured: the picker
 *    shows no choice and asks for one; a stale id is never re-sent.
 *  - **An empty picker states why** — no provider selected, or the provider's own reason
 *    (brand-scrubbed at render) with a Try again — never an empty dropdown.
 *  - **Provider off:** the coach can still be configured, and a note says nothing will
 *    sync until the tenant provider is set (with a link to Tenant settings).
 *
 * The write is the staff entity PUT (`updateStaff`) carrying the record's current
 * names, contact AND staff-login link as loaded, plus only the calendar fields that
 * changed. The link must ride along: the server applies `authUserId` from the request
 * unconditionally (an absent key reads as null, which clears it) — only the two calendar
 * fields are null-means-unchanged. A save refetches the record; the parent remounts this
 * panel on the calendar fields so it reseeds.
 *
 * **U3 — prove it, before a customer does.** "Send a test appointment" runs the real
 * booking path once against the SAVED settings (`POST …/calendar-test`, DNKAPI-0543
 * U4): the calendar's owner is verified, one plainly-titled 15-minute appointment is
 * created and then deleted with the cancellation call, and the result is reported in
 * plain words. When the automatic delete fails the appointment is still on the
 * calendar; the server answers an explicit remove action, which this panel parks
 * (`calendarTestPending`) so it **stays available until it is used** — across a route
 * change, never on a timer. Refusals name the missing precondition in the operator's
 * words (`staffCalendarTestErrorMessage`). The test action is offered ONLY to a
 * non-STAFF session (`useStaffTier`): it writes to a real person's calendar. (The whole
 * panel already sits under `RequireAdmin` via StaffDetail; the tier check is the belt.)
 *
 * **Embedded in a drawer (DNKADM-0494).** With `embedded`, the panel renders no Save of its
 * own: the Staff page's Booking calendar drawer owns the one Save and drives it through
 * `handleRef`. The PUT body is the same. `onStatusChange` reports when the form holds the
 * state the old Save button refused (sync on with no calendar), so the drawer holds its Save
 * off the same way. A failure that would have raised a toast shows inline instead; the
 * drawer raises the success toast. The calendar test stays inside the drawer, unchanged.
 */
import { useCallback, useEffect, useId, useImperativeHandle, useState, type Ref } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useStaffTier } from '@/auth/authHooks';
import { useResource } from '@/hooks/useResource';
import { updateStaff, type StaffInput, type V2Staff } from '@/api/staff';
import {
  getStaffCalendars,
  removeStaffCalendarTestEvent,
  sendStaffCalendarTest,
  type StaffCalendarTestRemoval,
  type StaffCalendarTestResult,
} from '@/api/staffCalendar';
import type { ShowToast } from '@/components/toastContext';
import { scrubBrand } from '@/utils/whitelabel';
import {
  staffCalendarSyncErrorMessage,
  staffCalendarTestErrorMessage,
} from './staffError';
import {
  clearPendingCalendarTestRemoval,
  getPendingCalendarTestRemoval,
  setPendingCalendarTestRemoval,
} from './calendarTestPending';
import { ScreenError } from '@/components/ScreenError';
import { classifyFormError } from '@/api/screenError';
import type { StaffEditorHandle } from './staffEditorHandle';

interface StaffCalendarSyncPanelProps {
  /** The loaded staff record (its calendar fields seed the form). */
  staff: V2Staff;
  /** Re-fetch the record after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
  /** Render without a Save button; the owning drawer saves through `handleRef` (DNKADM-0494). */
  embedded?: boolean;
  /** The drawer's handle (embedded mode). */
  handleRef?: Ref<StaffEditorHandle>;
  /** Reports whether the form holds a state Save must refuse (embedded mode). */
  onStatusChange?: (status: { blocked: boolean }) => void;
}

/** The `<Select>` value standing for "no calendar chosen" (never a real id). */
const NO_CALENDAR = '';

/** What the test result panel shows: a severity + plain words, and whether a removal is pending. */
interface TestView {
  severity: 'success' | 'warning' | 'info' | 'error';
  message: string;
}

/**
 * Plain words for a test report (DNKAPI-0543 U4 outcomes). The server's own sentence
 * (brand-scrubbed) covers an outcome this console does not know.
 */
function testResultView(result: StaffCalendarTestResult): TestView {
  const owner = result.ownerName !== null ? ` The calendar belongs to ${result.ownerName}.` : '';
  switch (result.outcome) {
    case 'created_and_deleted':
      return {
        severity: 'success',
        message: `The test appointment reached this staff member’s calendar and was removed again — the booking path works end to end.${owner}`,
      };
    case 'created_delete_failed':
      return {
        severity: 'warning',
        message: `The test appointment reached this staff member’s calendar but couldn’t be removed automatically. It is still there — check the calendar on their phone, then remove it below.${owner}`,
      };
    case 'verified_no_contact':
      return {
        severity: 'info',
        message: `The calendar and its owner were verified, but your login has no contact record to book with, so no test appointment was created.${owner}`,
      };
    default:
      return {
        severity: 'info',
        message: scrubBrand(result.message) || 'The calendar test finished.',
      };
  }
}

export function StaffCalendarSyncPanel({
  staff,
  refetch,
  showToast,
  embedded = false,
  handleRef,
  onStatusChange,
}: StaffCalendarSyncPanelProps) {
  const ids = useId();
  const staffId = staff.id;
  // The test action writes to a real person's calendar: never offered to a STAFF session.
  const canTest = !useStaffTier();

  // What the active provider offers for this coach, by name. `provider` rides along so
  // the panel can say "no provider" without a second read of tenant settings.
  const catalog = useResource(
    useCallback(() => getStaffCalendars(staffId), [staffId]),
    [staffId],
  );

  const storedId = staff.calendarId;
  const [syncEnabled, setSyncEnabled] = useState(staff.calendarSyncEnabled);
  const [selectedId, setSelectedId] = useState(storedId ?? NO_CALENDAR);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // U3 state. The pending removal lives in the module store (survives a route change and
  // this panel's save-remount); the last result's words are this mount's only.
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [testView, setTestView] = useState<TestView | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<StaffCalendarTestRemoval | null>(() =>
    getPendingCalendarTestRemoval(staffId),
  );

  const catalogData = catalog.status === 'ready' ? catalog.data : null;
  const calendars = catalogData?.calendars ?? [];
  const providerOff = catalogData !== null && catalogData.provider === 'none';
  const offered = (id: string) => calendars.some((c) => c.id === id);
  // The selection the picker can stand behind: one of the calendars it lists.
  const selectionKnown = selectedId !== NO_CALENDAR && offered(selectedId);
  // The stored selection is not in the current provider's list — needs re-selection.
  const storedMissing = storedId !== null && catalogData !== null && calendars.length > 0 && !offered(storedId);
  // The stored selection cannot be shown (the list is unavailable) — say so, honestly.
  const storedUnlisted = storedId !== null && catalogData !== null && calendars.length === 0;

  const calendarChanged = selectionKnown && selectedId !== storedId;
  const dirty = syncEnabled !== staff.calendarSyncEnabled || calendarChanged;
  // On-without-a-calendar is never offered: Save stays disabled until a calendar is picked.
  const canSave = dirty && (!syncEnabled || selectionKnown) && !saving;
  // The state the old Save button stayed disabled for: a change, but sync on with no calendar.
  const blocked = dirty && syncEnabled && !selectionKnown;

  useEffect(() => {
    onStatusChange?.({ blocked });
  }, [onStatusChange, blocked]);

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    setCalendarError(null);
    // Nothing changed: nothing to send (embedded Save closes the drawer).
    if (!dirty) return true;
    if (!canSave) return false;
    const input: StaffInput = { firstName: staff.firstName, lastName: staff.lastName };
    if (staff.email !== undefined) input.email = staff.email;
    if (staff.phone !== undefined) input.phone = staff.phone;
    // The staff-login link is re-sent as loaded (QA touch-1): dink-api's `applyOptional`
    // sets `authUserId` from the request unconditionally, and Jackson binds an absent
    // key and an explicit null identically on the record — so OMITTING it would silently
    // unlink the coach on every calendar save. Only the two calendar fields are
    // null-means-unchanged. `null` stays `null` (a no-op on an unlinked coach).
    input.authUserId = staff.authUserId;
    input.calendarSyncEnabled = syncEnabled;
    // Only an id the picker offered, and only when it changed — turning sync off sends
    // the flag alone, so the stored selection is kept (spec U2).
    if (calendarChanged) input.calendarId = selectedId;
    setSaving(true);
    try {
      await updateStaff(staffId, input);
      if (!embedded) showToast('Calendar sync saved', 'success');
      refetch();
      return true;
    } catch (err) {
      const fieldMessage = staffCalendarSyncErrorMessage(err);
      if (fieldMessage !== null) {
        setCalendarError(fieldMessage);
        return false;
      }
      const view = classifyFormError(err, 'Calendar sync couldn’t be saved. Please try again.');
      if (view.kind === 'inline' || embedded) setServerError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(handleRef, () => ({ save: handleSave }));

  async function handleTest() {
    setTestView(null);
    setTesting(true);
    try {
      const result = await sendStaffCalendarTest(staffId);
      setTestView(testResultView(result));
      if (result.remove !== null) {
        setPendingCalendarTestRemoval(staffId, result.remove);
        setPendingRemoval(result.remove);
      }
    } catch (err) {
      setTestView({
        severity: 'error',
        message: staffCalendarTestErrorMessage(
          err,
          'The test appointment couldn’t be sent. Please try again.',
        ),
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleRemove() {
    if (pendingRemoval === null) return;
    setRemoving(true);
    try {
      await removeStaffCalendarTestEvent(staffId, pendingRemoval);
      clearPendingCalendarTestRemoval(staffId);
      setPendingRemoval(null);
      setTestView({ severity: 'success', message: 'The test appointment was removed from the calendar.' });
    } catch (err) {
      // The removal stays available — the operator can try again once the cause is fixed.
      setTestView({
        severity: 'error',
        message: staffCalendarTestErrorMessage(
          err,
          'The test appointment couldn’t be removed. Please try again.',
        ),
      });
    } finally {
      setRemoving(false);
    }
  }

  const calendarLabelId = `${ids}-calendar-label`;
  const busy = saving || testing || removing;
  const noCalendarPlaceholder =
    calendars.length === 0 ? 'No calendars available' : 'Choose a calendar';

  /** Why the picker has nothing to offer — never an empty dropdown with no explanation. */
  function emptyPickerReason(): string | null {
    if (catalogData === null || calendars.length > 0) return null;
    if (providerOff) {
      return 'No calendar provider is selected for this tenant, so there are no calendars to choose from.';
    }
    if (catalogData.unavailableReason !== null) return scrubBrand(catalogData.unavailableReason);
    return 'The calendar provider offers no calendars for this staff member yet.';
  }
  const emptyReason = emptyPickerReason();

  /** The picker's helper line: an error first, then the state of the stored selection. */
  function pickerHelper(): string {
    if (calendarError !== null) return calendarError;
    if (storedMissing) {
      return 'The saved calendar isn’t offered by the current provider. Choose a calendar again.';
    }
    if (storedUnlisted) {
      return 'A calendar is saved for this staff member, but the list couldn’t be loaded to show it.';
    }
    if (emptyReason !== null) return emptyReason;
    return 'Bookings for this staff member go to the calendar you choose here.';
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Paper elevation={1} sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          {providerOff && (
            <Alert severity="info">
              The tenant calendar provider is off, so nothing will sync for any staff member —
              whatever is set here — until a provider is chosen in{' '}
              <MuiLink component={RouterLink} to="/tenant-settings">
                Tenant settings
              </MuiLink>
              .
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={syncEnabled}
                onChange={(event) => setSyncEnabled(event.target.checked)}
                disabled={saving}
              />
            }
            label="Calendar sync"
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: -1.5 }}>
            {syncEnabled
              ? 'On: confirmed bookings for this staff member are mirrored onto the calendar below.'
              : 'Off pauses syncing. The calendar choice is kept, so turning sync back on needs no re-selection.'}
          </Typography>

          {catalog.status === 'loading' && (
            <Skeleton
              variant="rounded"
              height={40}
              role="status"
              aria-label="Loading calendars"
              sx={{ borderRadius: 1 }}
            />
          )}

          {catalog.status === 'error' && (
            <ScreenError
              error={catalog.error}
              what="the calendars"
              onRetry={catalog.refetch}
              sx={{ alignItems: 'center' }}
            />
          )}

          {catalogData !== null && (
            <FormControl
              size="small"
              fullWidth
              disabled={saving || calendars.length === 0}
              error={calendarError !== null || (syncEnabled && storedMissing)}
            >
              {/* `shrink` + `notched` are not cosmetic here (DNKADM-0393 U4).
                  `NO_CALENDAR` is the empty string, so with nothing chosen MUI's
                  FormControl reads the Select as empty and leaves the label
                  unshrunk, centred inside the field — directly on top of the
                  "Choose a calendar" placeholder that `displayEmpty`/`renderValue`
                  draws in that same spot, which is the two-labels-over-each-other
                  the staging pass caught. `displayEmpty` means this field is NEVER
                  visually empty, so the label belongs in the outline's notch at all
                  times, and one legible label shows. */}
              <InputLabel id={calendarLabelId} shrink>
                Calendar
              </InputLabel>
              <Select
                labelId={calendarLabelId}
                value={selectionKnown ? selectedId : NO_CALENDAR}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  setCalendarError(null);
                }}
                input={<OutlinedInput label="Calendar" notched />}
                // "No choice" is a real state here (nothing chosen yet, or the stored
                // choice is stale), so it is shown as a placeholder — never a blank box.
                displayEmpty
                renderValue={(value) =>
                  value === NO_CALENDAR
                    ? noCalendarPlaceholder
                    : scrubBrand(calendars.find((c) => c.id === value)?.name ?? '')
                }
              >
                <MenuItem value={NO_CALENDAR} disabled>
                  {noCalendarPlaceholder}
                </MenuItem>
                {calendars.map((calendar) => (
                  <MenuItem key={calendar.id} value={calendar.id}>
                    {scrubBrand(calendar.name)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{pickerHelper()}</FormHelperText>
            </FormControl>
          )}

          {catalogData !== null && emptyReason !== null && !providerOff && (
            <Box>
              <Button size="small" onClick={catalog.refetch} disabled={saving}>
                Try again
              </Button>
            </Box>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}

          {!embedded && (
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => void handleSave()} disabled={!canSave}>
                {saving ? 'Saving…' : 'Save calendar sync'}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {canTest && (
        <Paper elevation={1} sx={{ p: 2.5, mt: 2 }}>
          <Stack spacing={1.5}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>Test this calendar</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sends one 15-minute test appointment to this staff member’s saved calendar and
              removes it again. It tests the saved settings — save any changes above first.
            </Typography>
            <Box>
              <Button variant="outlined" onClick={() => void handleTest()} disabled={busy}>
                {testing ? 'Sending…' : 'Send a test appointment'}
              </Button>
            </Box>

            {testView !== null && <Alert severity={testView.severity}>{testView.message}</Alert>}

            {pendingRemoval !== null && (
              <Alert
                severity="warning"
                sx={{ alignItems: 'center' }}
                action={
                  <Button color="inherit" size="small" onClick={() => void handleRemove()} disabled={busy}>
                    {removing ? 'Removing…' : 'Remove the test appointment'}
                  </Button>
                }
              >
                A test appointment is still on this staff member’s calendar. Remove it when you have
                checked it.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
